import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";

const EMPTY_COURTS: never[] = [];

export function useOrgHall() {
  const { organization } = useOrganization();

  const { data: hall, isLoading: hallLoading } = useQuery({
    queryKey: ["org-hall", organization?.id],
    queryFn: async () => {
      // Scope to the current organization (resolved via organization_members in
      // useOrganization). Returns the earliest-created hall for that org.
      let query = supabase.from("halls").select("*");
      if (organization?.id) {
        query = query.eq("organization_id", organization.id);
      }
      const { data, error } = await query
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!organization?.id,
    retry: 3,
  });

  const { data: courts = EMPTY_COURTS, isLoading: courtsLoading } = useQuery({
    queryKey: ["org-courts", hall?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courts")
        .select("*, halls(name, opening_time, closing_time)")
        .eq("hall_id", hall!.id)
        .order("sort_order");

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hall?.id,
    retry: 3,
  });

  const hallHours = useMemo(() => {
    if (!hall) return null;
    const c = parseInt(hall.closing_time?.split(":")[0] ?? "0", 10);
    return {
      start: parseInt(hall.opening_time?.split(":")[0] ?? "6", 10),
      end: c === 0 ? 24 : c,
    };
  }, [hall?.opening_time, hall?.closing_time]);

  const hallId = hall?.id ?? null;
  const isLoading = hallLoading || courtsLoading;

  return useMemo(() => ({
    organization,
    hall: hall ?? null,
    courts,
    hallId,
    hallHours,
    isLoading,
  }), [organization, hall, courts, hallId, hallHours, isLoading]);
}
